import React from 'react';
import { useProject } from '../../context/ProjectContext';
import type { ComponentEvent, RestAction } from '../../types/project';
import PageEventEditor from './PageEventEditor';
import styles from './VisualEditor.module.css';

type Props = {
  events: ComponentEvent[] | undefined;
  actions: RestAction[];
  onEventsChange: (events: ComponentEvent[]) => void;
};

function CommandIcon({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className={styles.commandIcon} aria-hidden="true">{children}</span>;
}

export default function PageManager({ events, actions, onEventsChange }: Props): React.ReactElement {
  const {
    doc, activePageId, setActivePage, addPage, duplicatePage,
    updatePage, movePage, deletePage, setDefaultPage,
  } = useProject();
  const pages = [...doc.project.pages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const activeIndex = pages.findIndex((page) => page.id === activePageId);
  const activePage = pages[activeIndex] ?? pages[0];
  const defaultPageId = doc.project.settings.defaultPageId ?? pages[0]?.id;
  const isDefault = activePage?.id === defaultPageId;

  if (!activePage) return <></>;

  const confirmDelete = () => {
    if (window.confirm(`Delete page "${activePage.name}"? Page components and page-scoped variables will be removed.`)) {
      deletePage(activePage.id);
    }
  };

  return (
    <section className={styles.commandRibbon} aria-label="Visual Editor command ribbon">
      <div className={styles.ribbonGroups}>
        <div className={[styles.ribbonGroup, styles.pagesGroup].join(' ')} role="group" aria-labelledby="pages-group-label">
          <div className={styles.ribbonGroupBody}>
            <label className={styles.pagePickerLabel}>
              <span className={styles.srOnly}>Active page</span>
              <select className={styles.pagePicker} aria-label="Active page" value={activePageId} onChange={(event) => setActivePage(event.target.value)}>
                {pages.map((page) => <option key={page.id} value={page.id}>{page.name}{defaultPageId === page.id ? ' (default)' : ''}</option>)}
              </select>
            </label>
            <button type="button" className={styles.commandButton} onClick={addPage}><CommandIcon>＋</CommandIcon><span>Add page</span></button>
            <button type="button" className={styles.commandButton} onClick={() => duplicatePage(activePageId)}><CommandIcon>⧉</CommandIcon><span>Duplicate</span></button>
            <div className={styles.commandPair} aria-label="Reorder page">
              <button type="button" className={styles.iconButton} disabled={activeIndex === 0} onClick={() => movePage(activePageId, -1)} title={activeIndex === 0 ? 'This page is already first' : 'Move page left'} aria-label="Move page left">←</button>
              <button type="button" className={styles.iconButton} disabled={activeIndex === pages.length - 1} onClick={() => movePage(activePageId, 1)} title={activeIndex === pages.length - 1 ? 'This page is already last' : 'Move page right'} aria-label="Move page right">→</button>
            </div>
          </div>
          <div className={styles.ribbonGroupLabel} id="pages-group-label">Pages</div>
        </div>

        <div className={[styles.ribbonGroup, styles.settingsGroup].join(' ')} role="group" aria-labelledby="settings-group-label">
          <div className={styles.ribbonGroupBody}>
            <label className={styles.ribbonField}><span>Name</span><input aria-label="Page name" value={activePage.name} onChange={(event) => updatePage(activePage.id, { name: event.target.value })} /></label>
            <label className={styles.ribbonField}><span>URL slug</span><input aria-label="Page URL slug" pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={activePage.slug ?? activePage.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')} onChange={(event) => updatePage(activePage.id, { slug: event.target.value })} /></label>
            <div className={styles.pageSettingActions}>
              <button type="button" className={[styles.commandButton, isDefault ? styles.commandButtonSelected : ''].join(' ')} disabled={isDefault} onClick={() => setDefaultPage(activePageId)} title={isDefault ? 'This is the default page' : 'Open this page when the application starts'}><CommandIcon>★</CommandIcon><span>{isDefault ? 'Default page' : 'Set default'}</span></button>
              <label className={styles.toggleControl}><input type="checkbox" checked={activePage.showInNavigation !== false} onChange={(event) => updatePage(activePage.id, { showInNavigation: event.target.checked })} /><span className={styles.toggleTrack} aria-hidden="true" /><span>In navigation</span></label>
            </div>
          </div>
          <div className={styles.ribbonGroupLabel} id="settings-group-label">Page settings <code>{activePage.id}</code></div>
        </div>

        <div className={[styles.ribbonGroup, styles.lifecycleGroup].join(' ')} role="group" aria-labelledby="lifecycle-group-label">
          <div className={styles.ribbonGroupBody}><PageEventEditor events={events} actions={actions} onChange={onEventsChange} /></div>
          <div className={styles.ribbonGroupLabel} id="lifecycle-group-label">Lifecycle</div>
        </div>

        <div className={[styles.ribbonGroup, styles.dangerGroup].join(' ')} role="group" aria-labelledby="danger-group-label">
          <div className={styles.ribbonGroupBody}>
            <button type="button" className={styles.dangerCommand} disabled={pages.length === 1} onClick={confirmDelete} title={pages.length === 1 ? 'A project must contain at least one page' : `Delete ${activePage.name}`}><CommandIcon>×</CommandIcon><span>Delete page</span></button>
          </div>
          <div className={styles.ribbonGroupLabel} id="danger-group-label">Page actions</div>
        </div>
      </div>
    </section>
  );
}
